import React, { ReactNode, useState, useMemo } from 'react';
import { SortState } from '../utils/tableUtils';
import { Trophy, ChevronDown, ChevronUp, Edit2, Trash2, MoreVertical } from "lucide-react";

// --- Interfaces ---
export interface TableColumn<T> {
  key: keyof T | string;
  title: string;
  render: (item: T, index: number) => ReactNode;
  sortable?: boolean;
  width?: string;
  rawCell?: boolean;
}

export interface TableActions {
  onEdit?: () => void;
  onDelete?: () => void;
}

interface TableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  keyExtractor: (item: T) => string | number;
  title?: string;
  /** Message when there is no data */
  emptyMessage?: string;
  isLoading?: boolean;
  sortState?: SortState<T>;
  onSort?: (key: keyof T) => void;
  searchTerm?: string;
  compact?: boolean;
  highlightCondition?: (item: T) => boolean;
  withRanks?: boolean;
  actions?: TableActions;
  qualifyingPositions?: number;
  /** Allow collapsing the table from the header (internal use, no UI config) */
  collapsible?: boolean;
  /** Initial collapsed state when collapsible (defaults to false) */
  defaultCollapsed?: boolean;
  /**
   * When collapsed:
   * - false (default): hide all rows (title/header only)
   * - true: show only highlighted rows (via highlightCondition)
   */
  collapsedShowHighlightedOnly?: boolean;
  rowLimit?: number;
  legend?: { color: string; label: string }[];
  className?: string;
  tableFixed?: boolean;
  fullWidth?: boolean;
}

function Table<T>({
  data,
  columns,
  keyExtractor,
  title,
  emptyMessage = 'No data found',
  isLoading = false,
  sortState,
  onSort,
  searchTerm,
  compact,
  highlightCondition,
  withRanks = false,
  actions,
  qualifyingPositions = 0,
  collapsible = false,
  defaultCollapsed = false,
  collapsedShowHighlightedOnly = false,
  rowLimit = 5,
  legend,
  className,
  tableFixed = false,
  fullWidth = false,
}: TableProps<T>) {

  const [isExpanded, setIsExpanded] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(defaultCollapsed);
  const [manuallyCollapsed, setManuallyCollapsed] = useState(false);
  const DEFAULT_ROW_LIMIT = rowLimit;

  const visibleData = useMemo(() => {
    if (isCollapsed) {
      // Manual collapse always hides all rows
      if (manuallyCollapsed || !collapsedShowHighlightedOnly || !highlightCondition) {
        return [] as T[];
      }
      return data.filter(highlightCondition);
    }

    if (isLoading || !highlightCondition || isExpanded) {
      return isExpanded ? data : data.slice(0, DEFAULT_ROW_LIMIT);
    }

    const topFive = data.slice(0, DEFAULT_ROW_LIMIT);
    const highlightedItems = data.filter(highlightCondition);
    const visibleKeys = new Set<string | number>();

    topFive.forEach(item => visibleKeys.add(keyExtractor(item)));
    highlightedItems.forEach(item => visibleKeys.add(keyExtractor(item)));

    return data.filter(item => visibleKeys.has(keyExtractor(item)));
  }, [data, isExpanded, highlightCondition, keyExtractor, isLoading, isCollapsed, collapsedShowHighlightedOnly]);

  const finalColumns = [
    ...(withRanks ? [{
      key: 'rank',
      title: '#',
      render: (_item: T, index: number) => (
        <span className="font-mono text-gray-400">
          {data.findIndex(d => keyExtractor(d) === keyExtractor(_item)) + 1}
        </span>
      ),
      sortable: false,
      width: tableFixed ? undefined : '1px',
      rawCell: false,
    }] : []),
    ...columns
  ];
      
  const hasMoreThanLimit = data.length > DEFAULT_ROW_LIMIT;

  return (
    <div className={`bg-stake-card shadow-lg mx-auto rounded-lg overflow-hidden border border-stake-border${fullWidth ? ' w-full max-w-full' : className ? ` ${className}` : ' max-w-xl'}`}>
      {title && (
        <div
          className={`px-4 py-3 bg-gray-900 border-b border-gray-700 flex items-center justify-between ${collapsible ? 'cursor-pointer select-none' : ''}`}
          onClick={collapsible ? () => { setIsCollapsed(prev => !prev); setManuallyCollapsed(true); } : undefined}
        >
          <div className="flex items-center gap-2">
            {collapsible && (
              <span className="p-1.5 text-gray-400">
                {isCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
              </span>
            )}
            <h2 className="text-lg font-semibold text-white">{title}</h2>
          </div>

          {legend && legend.length > 0 && (
            <div className="flex items-center gap-3 mr-2" onClick={e => e.stopPropagation()}>
              {legend.map((item) => (
                <div key={item.label} className="flex items-center gap-1">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${item.color}`} />
                  <span className="text-xs text-gray-400">{item.label}</span>
                </div>
              ))}
            </div>
          )}

          {actions && (actions.onEdit || actions.onDelete) && (
            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
              {actions.onEdit && (
                <button
                  onClick={actions.onEdit}
                  className="p-2 text-gray-400 hover:text-stake-blue hover:bg-dark-200 rounded transition-colors"
                  title="Edit"
                >
                  <Edit2 size={18} />
                </button>
              )}
              {actions.onDelete && (
                <button
                  onClick={actions.onDelete}
                  className="p-2 text-gray-400 hover:text-stake-red hover:bg-red-500/10 rounded transition-colors"
                  title="Delete"
                >
                  <Trash2 size={18} />
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {(!isCollapsed || (isCollapsed && collapsedShowHighlightedOnly && !manuallyCollapsed)) && (
        <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-stake-border" style={tableFixed ? { tableLayout: 'fixed', width: '100%' } : undefined}>
          <thead className="bg-dark-200">
            <tr>
              {finalColumns.map((column, idx) => (
                <th 
                  key={column.key === 'rank' ? 'rank-col' : column.key.toString()}
                  className={`
                    ${compact ? 'px-1 py-2' : 'px-4 py-3'} 
                    text-center text-xs font-medium text-gray-400 uppercase tracking-wider 
                    ${column.sortable && onSort ? 'cursor-pointer hover:bg-stake-card transition-colors' : ''}
                  `}
                  onClick={() => {
                    if (column.sortable && onSort && typeof column.key === 'string') {
                      onSort(column.key as keyof T);
                    }
                  }}
                  style={column.width ? { width: column.width } : undefined}
                >
                  <div className="flex items-center justify-center space-x-1">
                    <span>{column.title}</span>
                    {column.sortable && sortState && column.key === sortState.key && (
                      <span className="inline-flex ml-1 text-stake-blue">
                        {sortState.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          
          <tbody className="bg-stake-card">
            {isLoading ? (
              <tr>
                <td colSpan={finalColumns.length} className="px-4 py-4 text-center">
                  <div className="flex justify-center">
                    <Trophy className="animate-spin text-stake-blue h-6 w-6" />
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={finalColumns.length} className="px-4 py-4 text-center text-sm text-gray-300">
                  {searchTerm ? `No matching ${emptyMessage.toLowerCase()}` : emptyMessage}
                </td>
              </tr>
            ) : (
              visibleData.map((item, visibleIndex) => { 
                const isHighlighted = highlightCondition ? highlightCondition(item) : false;
                const actualIndex = data.findIndex(d => keyExtractor(d) === keyExtractor(item));
                const isQualifying = qualifyingPositions > 0 && actualIndex >= 0 && actualIndex < qualifyingPositions;
                
                let showGap = false;
                if (visibleIndex > 0) {
                  const previousItem = visibleData[visibleIndex - 1];
                  const previousActualIndex = data.findIndex(d => keyExtractor(d) === keyExtractor(previousItem));
                  if (actualIndex > previousActualIndex + 1) {
                    showGap = true;
                  }
                }

                return (
                  <React.Fragment key={keyExtractor(item)}>
                    {showGap && (
                      <tr className="bg-dark-200/20">
                        <td colSpan={finalColumns.length} className="py-1">
                          <div className="flex justify-center items-center text-gray-500 h-8">
                            <MoreVertical size={20} strokeWidth={3} className="opacity-30" />
                          </div>
                        </td>
                      </tr>
                    )}
                    <tr 
                      className={`transition-colors duration-150 border-b border-stake-border ${
                        isHighlighted ? 'bg-stake-blue/20' : 'hover:bg-dark-200'
                      }`}
                    >
                      {finalColumns.map((column, colIndex) => {
                        const isFirstColumn = colIndex === 0;
                        return (
                          <td
                            key={colIndex}
                            className={`
                              relative
                              ${compact ? 'px-1 py-2' : 'px-4 py-4'}
                              text-center text-sm ${fullWidth ? 'whitespace-normal break-words' : 'whitespace-nowrap'}
                              ${isHighlighted ? 'font-semibold' : ''}
                            `}
                            style={column.width ? { width: column.width, maxWidth: column.width, overflow: 'hidden' } : undefined}
                          >
                            {column.rawCell ? (
                              column.render(item, visibleIndex)
                            ) : (
                              <div className="flex justify-center">
                                {column.render(item, visibleIndex)}
                              </div>
                            )} 

                            {isQualifying && isFirstColumn && (
                              <div 
                                className="absolute right-0 top-1.5 bottom-1.5 w-[3px] bg-green-500 rounded-l-md shadow-[-2px_0_8px_rgba(34,197,94,0.4)]" 
                                style={{ zIndex: 10 }}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {hasMoreThanLimit && !isCollapsed && (
        <div className="px-4 py-2 text-center border-t border-stake-border">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center justify-center w-full text-sm font-medium text-stake-blue hover:text-stake-blue/80 transition-colors"
          >
            {isExpanded ? (
              <>
                Show Less ({DEFAULT_ROW_LIMIT} rows)
                <ChevronUp className="w-4 h-4 ml-1" />
              </>
            ) : (
              <>
                הצג הכל ({data.length} שורות)
                <ChevronDown className="w-4 h-4 ml-1" />
              </>
            )}
          </button>
        </div>
      )}
        </>
      )}
    </div>
  );
}

export default Table;